/**
 * Per-row CSV validation for the rescue pet-import wizard. Each input row
 * is sanitized and checked against the rules in `./constants.ts`, then
 * mapped to the camelCase shape `rescueImportPetsWithChips` expects.
 *
 * Errors are returned as `{ field, message }` objects (not bare strings)
 * so cell-level highlighting and per-cell tooltips can attribute each
 * issue to the right column without regex prefix-matching.
 */

import { isValidSerialNumber } from '@barkinglabs/nano-shared';
import validator from 'validator';
import type { Gender, Species } from '../../../../types/gql-op-types';
import type { RescueImportPetWithChipRowInput } from '../../../lib/integrationQueries';
import { lbsToKg, normalizePhoneDigits } from '../../../lib/utils';
import {
  CONTROL_CHARS,
  type CsvHeader,
  DAYS_IN_MONTH,
  DEFAULT_MONTH_OF_BIRTH,
  FORMULA_LEADER_EXEMPT_FIELDS,
  FORMULA_LEADERS,
  getMaxBirthYear,
  isLeapYear,
  MAX_LENGTHS,
  MAX_WEIGHT_LBS,
  MIN_BIRTH_YEAR,
  VALID_GENDERS,
  VALID_SPECIES,
} from './constants';
import { truncate } from './uiHelpers';

/**
 * Stable, message-independent failure category. Decouples the Status-pill
 * label (which is i18n-bound) from the human-readable error text. Adding
 * a new validation rule means adding a code here and a corresponding
 * label in `shortStatusFor` — no fuzzy string matching.
 */
export type ValidationCode =
  | 'missing'
  | 'invalid_format'
  | 'unknown'
  | 'duplicate'
  | 'owned_by_other_reseller'
  | 'already_linked'
  | 'not_usable'
  | 'too_long'
  | 'control_chars'
  | 'formula_leader'
  | 'out_of_range'
  | 'invalid_date';

/**
 * A row-level validation failure. `field` is the snake_case CSV column the
 * error is about; the cell-highlight + per-cell-tooltip code groups by
 * this. `code` is the stable kind (used for short labels); `message` is
 * the human-readable description shown in tooltips.
 */
export interface ValidationError {
  field: CsvHeader;
  code: ValidationCode;
  message: string;
}

export type ParsedRow = Omit<RescueImportPetWithChipRowInput, 'species' | 'gender' | 'yearOfBirth' | 'monthOfBirth'> & {
  rowIndex: number;
  /**
   * Backend expects `Species`/`Gender` enums; we hold them as the same
   * string union here. Validation guarantees they're one of the accepted
   * tokens before submit (see `toVerifiedRow`).
   */
  species: Species | '';
  gender: Gender | '';
  /**
   * Backend requires `Int!`, but we hold rows with parse errors here so
   * the table can render invalid input — null means missing or unparseable.
   * Narrowed to `number` by `toVerifiedRow` at submit time.
   */
  yearOfBirth: number | null;
  /**
   * Null when the cell was blank or invalid. `toVerifiedRow` substitutes
   * `DEFAULT_MONTH_OF_BIRTH` (January) at submit time for blank cells;
   * invalid values are caught by the `rawErrors` gate first.
   */
  monthOfBirth: number | null;
  rawErrors: ValidationError[];
};

/**
 * A `ParsedRow` whose nullable/empty-string fields have been narrowed —
 * matches the GraphQL `RescueImportPetWithChipRowInput` exactly. Produced
 * by `toVerifiedRow` and ready to send to the mutation.
 */
export type VerifiedRow = RescueImportPetWithChipRowInput;

/**
 * Narrows a `ParsedRow` to a `VerifiedRow`. Throws if the row still has
 * validation errors or any required field is still null/empty — the
 * caller must gate on `rawErrors.length === 0` first, so a throw here
 * means the gate is broken upstream.
 */
export function toVerifiedRow(r: ParsedRow): VerifiedRow {
  if (r.rawErrors.length > 0 || !r.species || !r.gender || r.yearOfBirth === null) {
    throw new Error(`row ${r.rowIndex} cannot be verified — submit gate is broken`);
  }
  return {
    name: r.name,
    chipSerialNumber: r.chipSerialNumber,
    species: r.species,
    gender: r.gender,
    ownerFirstName: r.ownerFirstName,
    ownerLastName: r.ownerLastName,
    ownerEmail: r.ownerEmail,
    ownerPhone: r.ownerPhone,
    yearOfBirth: r.yearOfBirth,
    weight: r.weight,
    breed: r.breed,
    // Blank/missing month coerces to January; invalid-format months
    // were rejected upstream by the `rawErrors` gate.
    monthOfBirth: r.monthOfBirth ?? DEFAULT_MONTH_OF_BIRTH,
    dayOfBirth: r.dayOfBirth,
    color: r.color,
    ownerStreet: r.ownerStreet,
    ownerApt: r.ownerApt,
    ownerCity: r.ownerCity,
    ownerState: r.ownerState,
    ownerZip: r.ownerZip,
  };
}

function pushError(errs: ValidationError[], field: CsvHeader, code: ValidationCode, message: string): void {
  errs.push({ field, code, message });
}

/**
 * Convert a stored `rowIndex` (file line number — header is line 1, data
 * rows start at line 2; matches the backend's per-row result index) to
 * the 1-based data-row number we show in the UI. Users think in terms of
 * "the first row" not "the first line in the file", so the review and
 * failure tables read more naturally with the header offset removed.
 */
export function displayRowNumber(rowIndex: number): number {
  return rowIndex - 1;
}

function sanitizeText(value: string, field: CsvHeader, errs: ValidationError[]): string {
  if (!value) {
    return value;
  }
  if (CONTROL_CHARS.test(value)) {
    pushError(errs, field, 'control_chars', `${field} contains invalid control characters`);
  }
  if (!FORMULA_LEADER_EXEMPT_FIELDS.has(field) && FORMULA_LEADERS.test(value)) {
    pushError(errs, field, 'formula_leader', `${field} cannot start with =, +, -, @, tab, or carriage return`);
  }
  const max = MAX_LENGTHS[field];
  if (max !== undefined && value.length > max) {
    pushError(errs, field, 'too_long', `${field} is too long (max ${max} characters)`);
  }
  return value;
}

// Strict numeric formats — `Number()` accepts scientific notation, hex,
// `Infinity`, and other JS-numeric shapes the user almost certainly didn't
// intend. We require plain digit strings so `'2e3'` doesn't silently become
// 2000.
const INT_PATTERN = /^-?\d+$/;
const FLOAT_PATTERN = /^-?\d+(\.\d+)?$/;

function parseOptionalInt(raw: string, field: CsvHeader, errs: ValidationError[]): number | null {
  const trimmed = raw.trim();
  if (!trimmed) {
    return null;
  }
  if (!INT_PATTERN.test(trimmed)) {
    pushError(errs, field, 'invalid_format', `${field} must be a whole number`);
    return null;
  }
  return Number(trimmed);
}

// `month_of_birth` is optional in the CSV. Returns `null` when the cell
// is blank OR contains an invalid value (with a pushed error in the
// latter case). The default-to-January coercion happens later in
// `toVerifiedRow`, so a row that the caller submits always carries a
// valid month while validation state stays unambiguous.
function parseMonthOrNull(raw: string, errs: ValidationError[]): number | null {
  const trimmed = raw.trim();
  if (!trimmed) {
    return null;
  }
  if (!INT_PATTERN.test(trimmed)) {
    pushError(errs, 'month_of_birth', 'invalid_format', 'month_of_birth must be a whole number');
    return null;
  }
  return Number(trimmed);
}

// Backend requires a non-null weight (in kg); the column is optional in the
// CSV and the value is in POUNDS to match the rest of the rescue portal.
// Missing/blank → 0 so the mutation always satisfies `weight: Float!`.
// Returns the parsed lbs value verbatim — the caller range-checks against
// `MAX_WEIGHT_LBS` and converts to kg via `lbsToKg` before assigning to
// the parsed row.
function parseWeightLbsOrDefault(raw: string, errs: ValidationError[]): number {
  const trimmed = raw.trim();
  if (!trimmed) {
    return 0;
  }
  if (!FLOAT_PATTERN.test(trimmed)) {
    pushError(errs, 'weight', 'invalid_format', 'weight must be a number');
    return 0;
  }
  return Number(trimmed);
}

export function validateAndMapRow(headers: CsvHeader[], cells: string[], rowIndex: number): ParsedRow {
  const errs: ValidationError[] = [];
  const get = (key: CsvHeader): string => {
    const idx = headers.indexOf(key);
    return idx === -1 ? '' : (cells[idx] ?? '').trim();
  };

  // ── Required: pet identity ──────────────────
  const name = sanitizeText(get('pet_name'), 'pet_name', errs);
  if (!name) {
    pushError(errs, 'pet_name', 'missing', 'pet_name is required');
  }

  const chipSerialNumber = get('microchip_id');
  // `isValidSerialNumber` covers Fi + third-party formats (9, 10, or
  // 15 hex chars). Any future format change belongs in the shared
  // package, not here.
  if (!chipSerialNumber) {
    pushError(errs, 'microchip_id', 'missing', 'microchip_id is required');
  } else if (!isValidSerialNumber(chipSerialNumber)) {
    pushError(errs, 'microchip_id', 'invalid_format', 'microchip_id is not a recognized microchip format');
  }

  const speciesRaw = sanitizeText(get('species'), 'species', errs);
  const speciesUpper = speciesRaw ? speciesRaw.toUpperCase() : '';
  if (!speciesUpper) {
    pushError(errs, 'species', 'missing', 'species is required');
  } else if (!VALID_SPECIES.has(speciesUpper)) {
    pushError(errs, 'species', 'invalid_format', `species must be one of: ${[...VALID_SPECIES].join(', ')}`);
  }
  const species: Species | '' = VALID_SPECIES.has(speciesUpper) ? (speciesUpper as Species) : '';

  const genderRaw = sanitizeText(get('gender'), 'gender', errs);
  const genderUpper = genderRaw ? genderRaw.toUpperCase() : '';
  if (!genderUpper) {
    pushError(errs, 'gender', 'missing', 'gender is required');
  } else if (!VALID_GENDERS.has(genderUpper)) {
    pushError(errs, 'gender', 'invalid_format', `gender must be one of: ${[...VALID_GENDERS].join(', ')}`);
  }
  const gender: Gender | '' = VALID_GENDERS.has(genderUpper) ? (genderUpper as Gender) : '';

  // ── Required: owner contact ─────────────────
  const ownerFirstName = sanitizeText(get('owner_first_name'), 'owner_first_name', errs);
  if (!ownerFirstName) {
    pushError(errs, 'owner_first_name', 'missing', 'owner_first_name is required');
  }

  const ownerLastName = sanitizeText(get('owner_last_name'), 'owner_last_name', errs);
  if (!ownerLastName) {
    pushError(errs, 'owner_last_name', 'missing', 'owner_last_name is required');
  }

  const ownerEmail = sanitizeText(get('owner_email'), 'owner_email', errs);
  if (!ownerEmail) {
    pushError(errs, 'owner_email', 'missing', 'owner_email is required');
  } else if (!validator.isEmail(ownerEmail)) {
    pushError(errs, 'owner_email', 'invalid_format', 'owner_email is not a valid email');
  }

  const ownerPhoneRaw = sanitizeText(get('owner_phone'), 'owner_phone', errs);
  // Accept any of (555) 123-4567 / 555-123-4567 / 5551234567 / +1 555 123 4567 —
  // normalize to digits and require a US-shape 10-digit result (or 11 starting
  // with 1, which `normalizePhoneDigits` strips back to 10). Reject 0-length
  // or anything outside that range so the contact is dialable.
  const ownerPhoneDigits = normalizePhoneDigits(ownerPhoneRaw);
  if (!ownerPhoneRaw) {
    pushError(errs, 'owner_phone', 'missing', 'owner_phone is required');
  } else if (ownerPhoneDigits.length !== 10) {
    pushError(errs, 'owner_phone', 'invalid_format', 'owner_phone must be a 10-digit US phone number');
  }

  // ── Pet attributes (year required, month defaults to 1, rest optional) ──
  const breed = sanitizeText(get('breed'), 'breed', errs);
  const color = sanitizeText(get('color'), 'color', errs);

  const yearOfBirthRaw = get('year_of_birth');
  const yearOfBirth = parseOptionalInt(yearOfBirthRaw, 'year_of_birth', errs);
  if (!yearOfBirthRaw.trim()) {
    pushError(errs, 'year_of_birth', 'missing', 'year_of_birth is required');
  }
  const monthOfBirth = parseMonthOrNull(get('month_of_birth'), errs);
  const dayOfBirth = parseOptionalInt(get('day_of_birth'), 'day_of_birth', errs);
  // Cell is in pounds (template label + every other rescue-portal weight
  // input is lbs); convert to kg before assigning to the parsed row so the
  // backend — which stores kg — receives a consistent unit.
  const weightLbs = parseWeightLbsOrDefault(get('weight'), errs);

  const maxBirthYear = getMaxBirthYear();
  if (yearOfBirth !== null && (yearOfBirth < MIN_BIRTH_YEAR || yearOfBirth > maxBirthYear)) {
    pushError(
      errs,
      'year_of_birth',
      'out_of_range',
      `year_of_birth must be between ${MIN_BIRTH_YEAR} and ${maxBirthYear}`,
    );
  }
  // Month is either parsed-as-number (range-check here) or null (the
  // submit-time default to January is applied by `toVerifiedRow`).
  const monthInRange = monthOfBirth !== null && monthOfBirth >= 1 && monthOfBirth <= 12;
  if (monthOfBirth !== null && !monthInRange) {
    pushError(errs, 'month_of_birth', 'out_of_range', 'month_of_birth must be 1-12');
  }
  if (dayOfBirth !== null && (dayOfBirth < 1 || dayOfBirth > 31)) {
    pushError(errs, 'day_of_birth', 'out_of_range', 'day_of_birth must be 1-31');
  }
  // Calendar correctness: reject impossible dates like Feb 30 or Apr 31.
  // Only runs when month + day are both in range so we don't double-report —
  // and only honors leap-year for Feb 29 when year is known.
  if (monthInRange && monthOfBirth !== null && dayOfBirth !== null && dayOfBirth >= 1 && dayOfBirth <= 31) {
    const maxDay = DAYS_IN_MONTH[monthOfBirth - 1];
    const isFeb29 = monthOfBirth === 2 && dayOfBirth === 29;
    const leapOk = isFeb29 ? yearOfBirth === null || isLeapYear(yearOfBirth) : true;
    if (dayOfBirth > maxDay || !leapOk) {
      pushError(
        errs,
        'day_of_birth',
        'invalid_date',
        `day_of_birth ${dayOfBirth} is not a valid day in month ${monthOfBirth}`,
      );
    }
  }
  if (weightLbs < 0 || weightLbs > MAX_WEIGHT_LBS) {
    pushError(errs, 'weight', 'out_of_range', `weight must be between 0 and ${MAX_WEIGHT_LBS} lbs`);
  }
  // The parsed row carries kg (the backend's storage unit). `lbsToKg` rounds
  // to one decimal; a 0 lbs input stays 0 kg, which is what the
  // missing/blank default expects.
  const weight = lbsToKg(weightLbs);

  // ── Optional: owner address ─────────────────
  const ownerStreet = sanitizeText(get('owner_street'), 'owner_street', errs);
  const ownerApt = sanitizeText(get('owner_apt'), 'owner_apt', errs);
  const ownerCity = sanitizeText(get('owner_city'), 'owner_city', errs);
  const ownerState = sanitizeText(get('owner_state'), 'owner_state', errs);
  const ownerZip = sanitizeText(get('owner_zip'), 'owner_zip', errs);

  return {
    rowIndex,
    // Required
    name,
    chipSerialNumber,
    species,
    gender,
    ownerFirstName,
    ownerLastName,
    ownerEmail,
    // Send normalized digits so the backend gets a consistent shape regardless
    // of how the user formatted the cell.
    ownerPhone: ownerPhoneDigits,
    // Optional pet
    breed: breed || null,
    yearOfBirth,
    monthOfBirth,
    dayOfBirth,
    weight,
    color: color || null,
    // Optional address
    ownerStreet: ownerStreet || null,
    ownerApt: ownerApt || null,
    ownerCity: ownerCity || null,
    ownerState: ownerState || null,
    ownerZip: ownerZip || null,
    rawErrors: errs,
  };
}

/** Returns the set of CSV columns that have at least one error in this row. */
export function errorFieldsForRow(errors: ValidationError[]): Set<CsvHeader> {
  const set = new Set<CsvHeader>();
  for (const e of errors) {
    set.add(e.field);
  }
  return set;
}

/** Groups a row's errors by the CSV column they're about. */
export function errorsByFieldForRow(errors: ValidationError[]): Map<CsvHeader, ValidationError[]> {
  const map = new Map<CsvHeader, ValidationError[]>();
  for (const e of errors) {
    const list = map.get(e.field) ?? [];
    list.push(e);
    map.set(e.field, list);
  }
  return map;
}

/**
 * Short human label for the Status column. Dispatches on the stable
 * (field, code) pair rather than message wording, so future message
 * changes (or i18n) don't silently fall back to truncation. Full error
 * text still appears in the hover tooltip.
 */
export function shortStatusFor(error: ValidationError): string {
  switch (error.field) {
    case 'microchip_id':
      switch (error.code) {
        case 'missing':
          return 'Missing microchip';
        case 'invalid_format':
          return 'Invalid microchip';
        case 'duplicate':
          return 'Duplicate microchip';
        case 'owned_by_other_reseller':
          return 'Registered to another rescue';
        case 'already_linked':
          return 'Chip already on a pet';
        case 'not_usable':
          return 'Chip is not importable';
        default:
          break;
      }
      break;
    case 'owner_email':
      return error.code === 'missing' ? 'Missing email' : 'Invalid email';
    case 'owner_phone':
      return error.code === 'missing' ? 'Missing phone' : 'Invalid phone';
    case 'owner_first_name':
      return 'Missing first name';
    case 'owner_last_name':
      return 'Missing last name';
    case 'species':
      return 'Invalid species';
    case 'gender':
      return 'Invalid sex';
    case 'breed':
      return 'Invalid breed';
    case 'pet_name':
      return 'Missing name';
    case 'month_of_birth':
      return 'Invalid birth month';
    default:
      break;
  }
  return truncate(error.message, 32);
}
